/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ArrowDownward } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Container, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, XAxis, YAxis, Tooltip, Brush, ResponsiveContainer } from 'recharts';
import { formatDate, helpers } from '../../../services/helpers/helpers';

interface ILineChart {
    data: any[],
    name: string,
    lineKey: string[],
    yAxis: string,
    yText: string,
    xAxis: string,
    multLine?: boolean,
}

export const LineChartInstrument = ({ data, name, lineKey, yAxis, yText, xAxis, multLine }: ILineChart) => {

    const convertBrazilianValue = (value: any): number | null => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        let numValue: number;

        if (typeof value === 'string') {
            const normalizedValue = value.replace(',', '.');
            numValue = parseFloat(normalizedValue);
        } else {
            numValue = Number(value);
        }

        if (isNaN(numValue) || !isFinite(numValue)) {
            return null;
        }

        return numValue;
    };

    const processedData = data
        .map(item => {
            const processedItem = { ...item };

            Object.keys(processedItem).forEach(key => {
                if (key !== xAxis && key !== 'data_leitura' && key !== 'nome') {
                    const convertedValue = convertBrazilianValue(processedItem[key]);
                    if (convertedValue !== null) {
                        processedItem[key] = convertedValue;
                    } else {
                        delete processedItem[key];
                    }
                }
            });

            return processedItem;
        })
        .filter(item => {
            return lineKey.some(key => typeof item[key] === 'number' && !isNaN(item[key]));
        });

    if (processedData.length === 0) {
        return (
            <Container>
                <Box padding={2}>
                    <Typography variant='h6'>{name}</Typography>
                    <div>Nenhum dado válido disponível para este instrumento</div>
                </Box>
            </Container>
        );
    }

    const calculateYDomain = () => {
        const allValues: number[] = [];

        processedData.forEach(item => {
            lineKey.forEach(key => {
                const value = item[key];
                if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
                    allValues.push(value);
                }
            });
        });

        if (allValues.length === 0) {
            return ['dataMin', 'dataMax'];
        }

        const min = Math.min(...allValues);
        const max = Math.max(...allValues);

        if (min === max) {
            const baseValue = min;
            return [baseValue - Math.abs(baseValue * 0.1), baseValue + Math.abs(baseValue * 0.1)];
        }

        const padding = Math.abs(max - min) * 0.1;
        return [min - padding, max + padding];
    };

    const [dotSize, setDotSize] = useState(5);
    const [lineSize, setLineSize] = useState(5);
    const [showDot, setShowDot] = useState(true);
    const [layout, setLayout] = useState<'horizontal' | 'vertical'>('horizontal');
    const [align, setAlign] = useState<'center' | 'left' | 'right'>('center');
    const [verticalAlign, setVerticalAlign] = useState<'bottom' | 'middle' | 'top'>('bottom');

    const handleChangeShowDot = () => {
        setShowDot(!showDot);
    };

    const handleDotSizeChange = (event: any) => {
        const newValue = parseInt(event.target.value);
        if (!isNaN(newValue) && newValue >= 0) {
            setDotSize(newValue);
        }
    };

    const handleLineSizeChange = (event: any) => {
        const newValue = parseInt(event.target.value);
        if (!isNaN(newValue) && newValue >= 1) {
            setLineSize(newValue);
        }
    };

    const handleLayoutChange = (event: any) => {
        setLayout(event.target.value);
    };

    const handleAlignChange = (event: any) => {
        setAlign(event.target.value);
    };

    const handleVerticalAlignChange = (event: any) => {
        setVerticalAlign(event.target.value);
    };

	// function formatXAxisDate(dateValue: any) {
	// 	if (typeof dateValue === 'string') {
	// 		return dateValue.split(' ')[0];
	// 	}
	// 	return dateValue;
	// }
    
    return (
        <Container>
            <Box width={'100%'} justifyContent={'center'} alignContent={'center'} padding={2} gap={1}>
                <Box width={'100%'}>
                    <Typography variant='h6'>{name}</Typography>
                    <ResponsiveContainer width={'100%'} height={300}>
                        <LineChart
                            data={processedData}
                            margin={{ top: 5, right: 50, left: 30, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey={xAxis}
                                tickFormatter={helpers.formatXAxisDate}
                                type="category"																
                            />
                            <YAxis
                                label={{
                                    value: yText,
                                    angle: -90,
                                    position: 'insideLeft',
                                    dy: 50,
                                    dx: -30
                                }}

                                domain={calculateYDomain()}
                                tickCount={10}
                                type="number"
                                allowDataOverflow={false}
                                allowDecimals={true}
																tickFormatter={(value) => value.toFixed(2)}
                            />
                            <Tooltip
                                contentStyle={{ color: 'black' }}
                                labelFormatter={(value) => `Data: ${formatDate(value)}`}
                                formatter={(value: any, name: string) => [
                                    typeof value === 'number' && !isNaN(value) ? value.toFixed(3) : 'N/A',
                                    name
                                ]}
                            />
                            {multLine ? (
                                <Legend
                                    layout={layout}
                                    align={align}
                                    verticalAlign={verticalAlign}
                                />
                            ) : null}
                            <Brush dataKey={xAxis} height={25} tickFormatter={formatDate} />
                            {lineKey.map(index => (
                                <Line
                                    key={index}
                                    type="monotone"
                                    dataKey={index}
                                    stroke={`#${Math.floor(Math.random() * 16777215).toString(16)}`}
                                    strokeWidth={lineSize}
                                    dot={showDot ? { strokeWidth: dotSize, r: dotSize } : false}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
                <Box width={'100%'}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ArrowDownward />}>
                            Personalização do Gráfico
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box marginTop={5}>
                                <Grid container spacing={2} padding={2}>
                                    <Grid item container alignItems={'center'} spacing={2} justifyContent={'center'}>
                                        <Grid item xs={4}>
                                            <TextField
                                                label='Tamanho do Marcador'
                                                value={dotSize}
                                                type='number'
                                                variant='filled'
                                                onChange={handleDotSizeChange}
                                                fullWidth
                                                inputProps={{ min: 0, max: 10 }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label='Tamanho da linha'
                                                value={lineSize}
                                                type='number'
                                                variant='filled'
                                                onChange={handleLineSizeChange}
                                                fullWidth
                                                inputProps={{ min: 1, max: 10 }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={showDot}
                                                            onChange={handleChangeShowDot}
                                                        />
                                                    }
                                                    label="Mostrar Marcador"
                                                />
                                            </FormGroup>
                                        </Grid>
                                    </Grid>
                                    {multLine ?
                                        <Grid item container spacing={2}>
                                            <Grid item xs={4}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Tipo de legenda</InputLabel>
                                                    <Select value={layout} onChange={handleLayoutChange} label='Tipo de legenda'>
                                                        <MenuItem value={'vertical'}>Vertical</MenuItem>
                                                        <MenuItem value={'horizontal'}>Horizontal</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Alinhamento X de legenda</InputLabel>
                                                    <Select value={align} onChange={handleAlignChange} label='Alinhamento X de legenda'>
                                                        <MenuItem value={'center'}>Central</MenuItem>
                                                        <MenuItem value={'left'}>Esquerda</MenuItem>
                                                        <MenuItem value={'right'}>Direita</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Alinhamento Y de legenda</InputLabel>
                                                    <Select value={verticalAlign} onChange={handleVerticalAlignChange} label='Alinhamento Y de legenda'>
                                                        <MenuItem value={'bottom'}>Baixo</MenuItem>
                                                        <MenuItem value={'top'}>Cima</MenuItem>
                                                        <MenuItem value={'middle'}>Meio</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        : null}
                                </Grid>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Box>
        </Container>
    );
};